import { AbleCommonProps } from '../Types';
import { LozengeProps } from '@able/react/dist/Lozenge';
import Lozenge from '@able/react/dist/Lozenge';
import Spacing from '@able/react/dist/Spacing';
import React, { PropsWithChildren, createContext } from 'react';
import './style.scss';
import { classNames } from '../util/helpers';

export type StaticDataType = {
    id?: string;
    name: string;
    helpText?: string;
    value?: (string | JSX.Element)[] | string;
    standAlone?: boolean;
    lozenge?: LozengeProps;
} & AbleCommonProps & PropsWithChildren;

export type ContextInitialValueType = {
    horizontalColumn?: number;
    /** Keeping for future purpose */
    // edit?: boolean;
}

const initialValue: ContextInitialValueType = { horizontalColumn: 1 };
export const StaticDataContext = createContext(initialValue);

/**
 * Static Data is a component can be used to make a data as name value pair and some other elements.
 *
 *
 * @version 1.0.0
 */

const StaticData = (props: StaticDataType) => {
    const { standAlone, id } = props;

    if (standAlone) {
        return <dl className='ted-ui-sd-group' id={id}>
            <StaticDataInternal {...props} />
        </dl>
    }

    let customAttr = {};
    if (id && !standAlone) {
        customAttr = {
            id: id
        }
    }

    return <StaticDataContext.Consumer>
        {() => <StaticDataInternal {...props} {...customAttr} />}
    </StaticDataContext.Consumer>
};

StaticData.displayName = 'StaticData';

const StaticDataInternal = (props: StaticDataType) => {
    const { name, value, lozenge, helpText, standAlone, children } = props;
    return <div className='ted-ui-sd' {...(!standAlone && { id: props.id })} >
        <dt>
            <span className="textStyleLabel">
                <strong>{name}</strong>
            </span>
        </dt>
        {helpText && <dd>
            <Spacing bottom='spacingHalf'>
                <span className="textStyleFinePrint">
                    {helpText}
                </span>
            </Spacing>
        </dd>}
        {children && <dd>{children}</dd>}
        {!children && value && <>
            <>
                {Array.isArray(value) ? (
                    value.map((ddValue, index) => {
                        return (<dd key={index}>
                            <Spacing bottom='spacingHalf'>
                                <span className="textStyleTextBodyShort">
                                    {ddValue}
                                </span>
                            </Spacing>
                        </dd>
                        );
                    })
                ) : (
                    <dd><Spacing bottom='spacingHalf' >
                        <span className="textStyleTextBodyShort">
                            {value}
                        </span>
                    </Spacing>
                    </dd>
                )}
            </>
        </>}
        {lozenge && <dd>
            <Spacing top='spacingHalf'>
                <Lozenge
                    {...lozenge}
                />
            </Spacing>
        </dd>}
    </div>;
}

export type StaticDataGroupProps = {
    id?: string;
    config?: ContextInitialValueType;
} & AbleCommonProps & PropsWithChildren;

const StaticDataGroup = (props: StaticDataGroupProps) => {
    const { children, config, id } = props;
    const configValue = config ?? {};
    let style: React.CSSProperties = {};

    configValue.horizontalColumn = configValue.horizontalColumn ?? 1;

    const horizontal = configValue.horizontalColumn > 1;

    let staticDataCount = 0;

    React.Children.forEach(children, (child: any) => {
        const displayName = child?.type?.displayName ?? '';
        if (displayName === "StaticData") {
            staticDataCount++;
        }
    });

    staticDataCount = configValue.horizontalColumn > staticDataCount ? staticDataCount : configValue.horizontalColumn;

    if (horizontal) {
        style = {
            gridTemplateColumns: `repeat(${staticDataCount}, minmax(auto, 100%))`
        }
    }

    return <dl className={classNames('ted-ui-sd-group', horizontal && 'StaticDataGroup_Horizontal')} style={style} id={id}>
        <StaticDataContext.Provider value={configValue}>
            {children}
        </StaticDataContext.Provider>
    </dl>
}
export { StaticDataGroup };
export default StaticData;